// The Briefing when a TASK is OPEN in the box (frame 40007893:378129, "step 6: user opens the task").
//
// Every other briefing answers "what am I looking at?" with counts that double as filters. This one
// answers a different question — "what IS this task?" — so it is a two-column grid of labelled
// facts rather than a list of rows: Due / Reminder / Created / Created from down the left, and
// where it LIVES down the right (In / Related / Attachments / Work-board).
//
// A fact is rendered only when the payload actually carries it. An empty labelled cell reads as a
// fault ("Reminder: —" says the reminder was lost, not that there is none), so a fact with no value
// is dropped entirely — the same rule briefings/rows.js applies to its rows. What the payload does
// NOT carry today is recorded in the sprint's dev notes against the BFF item, never guessed at here.
//
// Pure, over values the store already holds: the task record (v1/task), its story timeline event,
// the ongoing, the pack, the pack's research items, the task's documents and the Work-board's
// contents. Nothing mounted, so the whole grid is testable without the box.

import { shortClock } from '@/lib/timelineTime'
import { parseUtc } from '@/lib/dueStatus'
import { countLabel, countText } from './rows'

const norm = (t) => String(t || '').replace(/\s+/g, ' ').trim().toLowerCase()
// busy-my-web#609 (Saffet 2026-10-09): the shared `countLabel` — thousands separator, capital after the count.
// "2 Photos, 1 PDF" — a fact in a Briefing's grid is listed in the Briefing too.
const plural = (n, word) => countLabel(n, word)
// …and the same count in lower case, for the rail's cards (lib/entityTimeline.js), which are not a
// Briefing and keep the words they had — only the thousands separator is shared.
const lowerPlural = (n, word) => `${countText(n)} ${word}${Number(n) === 1 ? '' : 's'}`

/**
 * The frame's stamp for an instant: "Aug 21, 6pm" — month first, and no ":00" on a whole hour
 * (`shortClock`, the app's one compact clock). `withYear` adds the year for the document callout,
 * which the frame writes as "Aug 11 2026, 5pm": a date sitting inside a document has no
 * surrounding context to date it by, where the grid's facts are all read as "this task, now".
 */
export function factStamp(value, { withYear = false } = {}) {
  if (value === null || value === undefined || value === '') return ''
  const dt = value instanceof Date ? value : parseUtc(value)
  if (!dt || isNaN(dt.getTime())) return ''
  let day = ''
  try {
    day = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}) })
  } catch { return '' }
  // en-US puts a comma before the year ("Aug 11, 2026"); the frame does not.
  if (withYear) day = day.replace(/,\s*(\d{4})$/, ' $1')
  const clock = shortClock(dt)
  return clock ? `${day}, ${clock}` : day
}

/**
 * What is attached to the task, counted the way the frame words it ("2 photos").
 * Pictures are named as pictures — "2 files" for two photos hides what they are, and the chips on
 * the Work-board show thumbnails.
 *
 * `count` is `TaskResponse.attachmentCount` (busy-my-web#68) and is only a NUMBER — it says how
 * many there are, never what they are. So it answers on its own while the documents are still in
 * flight (or when nothing asked for them), and the moment the real list arrives that list wins,
 * because "2 photos" is the fact the frame writes and a count alone cannot say it. An old BFF sends
 * no count at all, and then this behaves exactly as it always did.
 *
 * `capital` is the Briefing's form — "2 Photos, 1 PDF" (busy-my-web#609, Saffet 2026-10-09: a capital
 * after every count in a briefing). The grids here pass it; the rail's Attachments line
 * (lib/entityTimeline.js) shares this function, is not a Briefing, and keeps "2 photos, 1 PDF".
 */
export function attachmentsText(docs, count = null, { capital = false } = {}) {
  const say = capital ? plural : lowerPlural
  const list = (docs || []).filter(Boolean)
  if (!list.length) {
    return Number.isFinite(count) && count > 0 ? say(count, 'attachment') : ''
  }
  const photos = list.filter(d => String(d.contentType || '').startsWith('image/')).length
  // …and a PDF is named as one (Pouria, 2026-10-02, the Task-timeline frame: "Attachments · 1 PDF"),
  // by its content type or, when the store kept none, its extension.
  const pdfs = list.filter(d => !String(d.contentType || '').startsWith('image/')
    && (/pdf/i.test(String(d.contentType || '')) || /\.pdf$/i.test(String(d.filename || d.fileName || '')))).length
  const rest = list.length - photos - pdfs
  return [
    photos ? say(photos, 'photo') : '',
    pdfs ? countLabel(pdfs, 'PDF') : '',
    rest ? say(rest, 'file') : '',
  ].filter(Boolean).join(', ')
}

/**
 * What is on the Work-board right now — "2 Research packs, 2 Photos". The fact is about the BOARD,
 * not about the task: the grid's right column says where the task sits and what is in hand beside
 * it, which is why this reads the picks and the selection rather than anything on the task.
 */
export function boardText({ packs = [], items = [] } = {}) {
  const of = (kind) => (items || []).filter(i => i && i.kind === kind).length
  return [
    packs?.length ? plural(packs.length, 'Research pack') : '',
    of('photo') ? plural(of('photo'), 'Photo') : '',
    of('link') ? plural(of('link'), 'Link') : '',
    of('note') ? plural(of('note'), 'Note') : '',
  ].filter(Boolean).join(', ')
}

/**
 * The pack item a task was raised FROM, or null.
 *
 * The event now NAMES it (`sourceItemId`, busy-my-web#68), and an id beats any amount of matching:
 * it survives the user rewriting the sentence the task came from, which the text rule cannot. An id
 * that names an item this pack does not hold is not a fallback case — the task was raised somewhere
 * else, and "Created from" says nothing rather than pointing at whichever item happens to read alike.
 *
 * Without an id (an older BFF, or a task not raised from an item at all) the original rule stands:
 * the text the mark bar kept as the event's `detail`, matched the same way packDocAnchor and
 * syncDocDeletions decide an item's identity — a solid slice of text.
 */
export function createdFromItem(event, packItems) {
  const sourceId = event?.sourceItemId
  if (sourceId) return (packItems || []).find(r => r && String(r.id) === String(sourceId)) || null

  return matchItemByText(event?.detail, packItems)
}

/**
 * The pack item whose own text carries `text` (or is carried by it), or null — the text-identity
 * rule on its own, so the document's mark bar can use it at the ONE moment it is certain: when a
 * task is being made out of a selection that is still in the document. What it records then is an
 * id (`sourceItemId`), which is why the rule does not have to hold later.
 */
export function matchItemByText(text, packItems) {
  const key = norm(text).slice(0, 60)
  if (key.length < 12) return null
  return (packItems || []).find((r) => {
    if (!r || r.kind === 'doc') return false
    const itemText = norm(r.content || r.title)
    return !!itemText && (itemText.includes(key) || key.includes(itemText.slice(0, 60)))
  }) || null
}

const fact = (key, label, value) => (String(value ?? '').trim() ? { key, label, value: String(value).trim() } : null)

/**
 * The facts grid for the open task.
 *
 * @param task      the task record (v1/task) — due, reminder, created, its own title
 * @param event     the story timeline event behind it, when the task was opened from a story/pack
 * @param ongoing   the ongoing whose story is open — the "In" fact
 * @param pack      the Research Pack open beside it, if any — the "Related" fact
 * @param packItems that pack's research items, for "Created from"
 * @param docs      the task's documents (v1/document/{timelineId}/list)
 * @param board     `{ packs, items }` — what the Work-board is holding
 * @returns `{ title, left, right }`, or null when there is no task in hand
 */
export function taskFacts({ task = null, event = null, ongoing = null, pack = null, packItems = [], docs = [], board = {} } = {}) {
  if (!task && !event) return null
  const title = String(task?.title || event?.title || '').trim()

  const source = createdFromItem(event, packItems)
  const left = [
    fact('due', 'Due', factStamp(task?.dueDate || event?.dueDate)),
    fact('reminder', 'Reminder', factStamp(task?.remindDate)),
    fact('created', 'Created', factStamp(task?.createDate || event?.createdDate)),
    // Both halves or nothing: "Prada Sunglasses" alone would only repeat the Related fact opposite.
    // The second half is the frame's own wording when the payload can give it ("Snippet 2" —
    // `sourceLabel`, the item's place in its pack, computed by the BFF on every read); the item's
    // title is the fallback for a payload that carries no label.
    fact('createdFrom', 'Created from',
      source && pack?.title ? `${pack.title} › ${event?.sourceLabel || source.title || 'Extraction'}` : ''),
  ].filter(Boolean)

  const right = [
    fact('in', 'In', ongoing?.title),
    fact('related', 'Related', pack?.title ? `${pack.title} · Research Pack` : ''),
    fact('attachments', 'Attachments', attachmentsText(docs, task?.attachmentCount, { capital: true })),
    fact('workboard', 'Work-board', boardText(board)),
  ].filter(Boolean)

  if (!left.length && !right.length) return null
  // The title names the task itself — the strip above the box already says which ongoing it is in.
  return { title: title ? `Briefing · ${title}` : 'Briefing', left, right }
}

export default taskFacts
